<script lang="ts" setup>
    import { ref, computed, onMounted, watch } from "vue"
    import {
        AdminLogic,
        type CompanySettings,
        type WebserviceOption,
    } from "~/logic/admin.logic"
    import { Alert } from "~/logic"

    /**
     * Fiche d'une entreprise : identite, liaison Lola, Google, Mattermost.
     * Sans `customerId`, le formulaire cree une nouvelle entreprise (inactive par defaut).
     */
    const props = defineProps<{
        customerId?: number | null
    }>()
    const emit = defineEmits<{ saved: [settings: CompanySettings] }>()

    const isCreate = computed(() => !props.customerId)

    const loading = ref(false)
    const saving = ref(false)
    const saved = ref<CompanySettings | null>(null)
    const webservices = ref<WebserviceOption[]>([])
    const apiErrors = ref<Record<string, string[]>>({})

    const emptyForm = () => ({
        key: "",
        name: "",
        active: false,
        color: "" as string | null,
        apikey: "",
        database_server: "" as string | null,
        database_name: "" as string | null,
        webservice_id: null as number | null,
        ggl_place_id: "" as string | null,
        ggl_email: "" as string | null,
        fb_url: "" as string | null,
        team_id_mattermost: "" as string | null,
        intervention_mail_actif: false,
        intervention_mail_destinataires: "" as string | null,
    })
    const form = ref(emptyForm())

    const webserviceItems = computed(() =>
        webservices.value.map((ws) => ({
            value: ws.id,
            title:
                ws.id === 5
                    ? `${ws.name} — LightREST (Lola 2027)`
                    : `${ws.name} — WebDev`,
            subtitle: ws.address ?? "",
        })),
    )
    const selectedWebservice = computed(() =>
        webservices.value.find((ws) => ws.id === form.value.webservice_id),
    )

    // Changements sensibles : signales avant l'enregistrement.
    const keyChanged = computed(
        () =>
            !isCreate.value &&
            !!saved.value?.key &&
            form.value.key !== saved.value.key,
    )
    const deactivating = computed(
        () => !isCreate.value && !!saved.value?.active && !form.value.active,
    )
    const routingChanged = computed(
        () =>
            !isCreate.value &&
            (form.value.webservice_id !== saved.value?.webservice_id ||
                (form.value.database_server || null) !==
                    (saved.value?.database_server || null) ||
                (form.value.database_name || null) !==
                    (saved.value?.database_name || null)),
    )

    const reviewUrl = computed(() =>
        form.value.ggl_place_id
            ? `https://search.google.com/local/writereview?placeid=${form.value.ggl_place_id}`
            : null,
    )

    function fieldError(field: string): string[] {
        return apiErrors.value[field] ?? []
    }

    function fill(settings: CompanySettings) {
        saved.value = settings
        form.value = {
            key: settings.key ?? "",
            name: settings.name ?? "",
            active: settings.active,
            color: settings.color ?? "",
            apikey: settings.apikey ?? "",
            database_server: settings.database_server ?? "",
            database_name: settings.database_name ?? "",
            webservice_id: settings.webservice_id,
            ggl_place_id: settings.ggl_place_id ?? "",
            ggl_email: settings.ggl_email ?? "",
            fb_url: settings.fb_url ?? "",
            team_id_mattermost: settings.team_id_mattermost ?? "",
            intervention_mail_actif: !!settings.intervention_mail_actif,
            intervention_mail_destinataires:
                settings.intervention_mail_destinataires ?? "",
        }
    }

    async function load() {
        loading.value = true
        apiErrors.value = {}
        diagnostic.value = null
        try {
            webservices.value = await AdminLogic.getWebservices()
            if (props.customerId) {
                fill(await AdminLogic.getCompanySettings(props.customerId))
            } else {
                saved.value = null
                form.value = emptyForm()
            }
        } catch (error) {
            await Alert.error("Impossible de charger la fiche de l'entreprise")
        } finally {
            loading.value = false
        }
    }

    async function save() {
        apiErrors.value = {}
        if (!colorValid.value) {
            await Alert.error("Couleur du thème : format attendu #RRGGBB")
            return
        }
        const warnings = [
            keyChanged.value &&
                `La clé passe de « ${saved.value?.key} » à « ${form.value.key} » : l'adresse ${saved.value?.key}.lola-france.fr cessera de fonctionner.`,
            deactivating.value &&
                "L'entreprise sera désactivée : tous ses personnels seront déconnectés immédiatement.",
        ].filter(Boolean)
        if (warnings.length) {
            const confirm = await Alert.confirm(
                "Confirmer l'enregistrement",
                warnings.join("\n\n"),
            )
            if (!confirm.isConfirmed) return
        }

        saving.value = true
        try {
            const { apikey, ...data } = form.value
            const result = isCreate.value
                ? await AdminLogic.createCompanySettings({ ...data, apikey })
                : await AdminLogic.updateCompanySettings(
                      props.customerId!,
                      data,
                  )
            fill(result)
            await Alert.success(
                isCreate.value ? "Entreprise créée" : "Fiche enregistrée",
            )
            emit("saved", result)
        } catch (error: any) {
            apiErrors.value = error?.body?.errors ?? {}
            const details = Object.values(apiErrors.value).flat()
            await Alert.error(
                details.length
                    ? `Erreur lors de l'enregistrement : ${details.join(" ")}`
                    : "Erreur lors de l'enregistrement",
            )
        } finally {
            saving.value = false
        }
    }

    // Diagnostic de liaison (port du serveur Lola, puis requete via le webservice).
    const diagnosing = ref(false)
    const diagnostic = ref<any>(null)

    async function runDiagnostic() {
        if (!props.customerId) return
        diagnosing.value = true
        diagnostic.value = null
        try {
            diagnostic.value = await AdminLogic.lolaCheck(props.customerId)
        } catch (error: any) {
            diagnostic.value = {
                incomplete: true,
                message: error?.body?.message ?? "Diagnostic impossible",
            }
        } finally {
            diagnosing.value = false
        }
    }

    async function copy(text: string | null | undefined, label: string) {
        if (!text) return
        await navigator.clipboard.writeText(text)
        await Alert.success(`${label} copié`)
    }

    // En-tete : couleur de l'entreprise (repli indigo), initiales, nature du webservice.
    const accent = computed(() =>
        /^#[0-9a-f]{6}$/i.test(form.value.color || "")
            ? (form.value.color as string)
            : "#5C6BC0",
    )
    // Couleur du theme : primaire de l'application du client et de son espace client.
    const HEX = /^#[0-9a-f]{6}$/i
    const colorMenu = ref(false)
    const colorSwatches = [
        ["#2C4C9B", "#1565C0", "#0277BD", "#00838F"],
        ["#00695C", "#2E7D32", "#558B2F", "#9E9D24"],
        ["#EF6C00", "#D84315", "#C62828", "#BD1734"],
        ["#AD1457", "#6A1B9A", "#4527A0", "#37474F"],
    ]
    const colorValid = computed(
        () => !form.value.color || HEX.test(form.value.color),
    )
    const colorRules = [
        (v: string | null) =>
            !v || HEX.test(v) || "Format attendu : #RRGGBB (ex. #2c4c9b)",
    ]
    const pickerColor = computed({
        get: () => accent.value,
        set: (v: string) => (form.value.color = v.slice(0, 7).toUpperCase()),
    })

    // Contraste WCAG avec le blanc : la couleur sert aussi de texte (liens, boutons
    // contour) sur fond blanc. Le texte pose SUR la couleur est choisi par Vuetify.
    function luminance(hex: string): number {
        const [r, g, b] = [1, 3, 5].map((i) => {
            const c = parseInt(hex.slice(i, i + 2), 16) / 255
            return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
        })
        return 0.2126 * r + 0.7152 * g + 0.0722 * b
    }
    const contrastWithWhite = computed(
        () => 1.05 / (luminance(accent.value) + 0.05),
    )
    // Texte pose sur la couleur (apercu) : blanc ou noir, le plus contraste des deux.
    const onAccent = computed(() =>
        contrastWithWhite.value >= (luminance(accent.value) + 0.05) / 0.05
            ? "#FFFFFF"
            : "#000000",
    )
    const contrastLevel = computed(() => {
        const r = contrastWithWhite.value
        if (r >= 4.5)
            return {
                color: "success",
                icon: "mdi-check-circle",
                text: "Lisible en texte sur fond blanc",
            }
        if (r >= 3)
            return {
                color: "warning",
                icon: "mdi-alert",
                text: "Juste lisible : correcte pour les boutons, faible pour les petits textes",
            }
        return {
            color: "error",
            icon: "mdi-alert-octagon",
            text: "Trop claire : les liens et textes de cette couleur seront peu lisibles",
        }
    })

    const initials = computed(
        () =>
            (form.value.name || "?")
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((w) => w[0])
                .join("")
                .toUpperCase() || "?",
    )
    const isLightrest = computed(() => selectedWebservice.value?.id === 5)

    onMounted(load)
    watch(() => props.customerId, load)
</script>

<template>
    <v-card
        :loading="loading"
        class="company-general"
        elevation="2"
        rounded="lg"
    >
        <!-- En-tete aux couleurs de l'entreprise -->
        <div
            class="cg-banner pa-4 d-flex align-center flex-wrap ga-4"
            :style="{
                background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 60%, ${accent}99 100%)`,
            }"
        >
            <v-avatar size="56" color="white" class="elevation-2">
                <span
                    class="text-h6 font-weight-bold"
                    :style="{ color: accent }"
                >
                    {{ initials }}
                </span>
            </v-avatar>
            <div class="flex-grow-1 text-white">
                <div class="text-h6 font-weight-bold">
                    {{
                        isCreate
                            ? form.name || "Nouvelle entreprise"
                            : form.name || "Fiche de l'entreprise"
                    }}
                </div>
                <div class="d-flex flex-wrap ga-2 mt-1">
                    <v-chip
                        v-if="form.key"
                        size="small"
                        variant="flat"
                        color="white"
                        prepend-icon="mdi-web"
                        :href="
                            isCreate
                                ? undefined
                                : (saved?.lolapp_url ?? undefined)
                        "
                        target="_blank"
                    >
                        {{ form.key }}.lola-france.fr
                    </v-chip>
                    <v-chip
                        v-if="!isCreate"
                        size="small"
                        variant="flat"
                        :color="saved?.active ? 'success' : 'grey-darken-1'"
                        :prepend-icon="
                            saved?.active
                                ? 'mdi-check-circle'
                                : 'mdi-pause-circle'
                        "
                    >
                        {{ saved?.active ? "Active" : "Inactive" }}
                    </v-chip>
                    <v-chip
                        v-if="selectedWebservice"
                        size="small"
                        variant="flat"
                        :color="isLightrest ? 'teal' : 'orange-darken-2'"
                        :prepend-icon="
                            isLightrest ? 'mdi-lightning-bolt' : 'mdi-server'
                        "
                    >
                        {{
                            isLightrest
                                ? "LightREST · Lola 2027"
                                : `WebDev ${selectedWebservice.name}`
                        }}
                    </v-chip>
                </div>
            </div>
            <v-switch
                v-model="form.active"
                color="white"
                base-color="white"
                class="text-white cg-switch"
                :label="form.active ? 'Active' : 'Inactive'"
                hide-details
                inset
            />
        </div>

        <v-card-text class="pa-4">
            <v-form @submit.prevent="save">
                <v-alert
                    v-if="isCreate"
                    type="info"
                    variant="tonal"
                    density="compact"
                    class="mb-4"
                    icon="mdi-information"
                >
                    Laisser l'entreprise <strong>inactive</strong> tant que le
                    serveur Lola n'est pas renseigné et que le diagnostic de
                    liaison n'est pas vert.
                </v-alert>

                <v-row>
                    <!-- Identite -->
                    <v-col cols="12" lg="6">
                        <div
                            class="cg-section"
                            :style="{ '--cg-color': accent }"
                        >
                            <div class="cg-section-title">
                                <v-avatar
                                    size="32"
                                    :color="accent"
                                    variant="tonal"
                                >
                                    <v-icon size="18"
                                        >mdi-card-account-details</v-icon
                                    >
                                </v-avatar>
                                Identité
                            </div>
                            <v-text-field
                                v-model="form.name"
                                label="Nom"
                                prepend-inner-icon="mdi-domain"
                                variant="outlined"
                                density="comfortable"
                                :color="accent"
                                :error-messages="fieldError('name')"
                            />
                            <v-text-field
                                v-model="form.key"
                                label="Clé (sous-domaine)"
                                prepend-inner-icon="mdi-web"
                                variant="outlined"
                                density="comfortable"
                                :color="accent"
                                :hint="
                                    form.key
                                        ? `https://${form.key}.lola-france.fr`
                                        : 'minuscules, chiffres et tirets'
                                "
                                persistent-hint
                                class="mb-2"
                                :error-messages="fieldError('key')"
                            />
                            <v-text-field
                                v-model="form.apikey"
                                label="Clé API (CLE.NOM_CTRE)"
                                prepend-inner-icon="mdi-key-variant"
                                variant="outlined"
                                density="comfortable"
                                :color="accent"
                                :readonly="!isCreate"
                                :bg-color="
                                    isCreate ? undefined : 'grey-lighten-4'
                                "
                                :hint="
                                    isCreate
                                        ? 'Valeur lue dans la base Lola du client ; nomme le stockage des fichiers'
                                        : 'Fixée à la création : elle nomme le stockage des fichiers'
                                "
                                persistent-hint
                                class="mb-2"
                                :error-messages="fieldError('apikey')"
                            >
                                <template v-if="!isCreate" #append-inner>
                                    <v-icon
                                        size="small"
                                        :color="accent"
                                        title="Copier"
                                        @click="copy(form.apikey, 'Clé API')"
                                    >
                                        mdi-content-copy
                                    </v-icon>
                                </template>
                            </v-text-field>
                            <v-text-field
                                v-model="form.color"
                                label="Couleur du thème"
                                variant="outlined"
                                density="comfortable"
                                :color="accent"
                                placeholder="#2C4C9B"
                                hint="Couleur principale de l'application du client et de son espace client"
                                persistent-hint
                                class="mb-2"
                                :rules="colorRules"
                                :error-messages="fieldError('color')"
                            >
                                <template #prepend-inner>
                                    <span
                                        class="color-dot"
                                        :style="{ background: accent }"
                                        title="Choisir une couleur"
                                        @click="colorMenu = true"
                                    />
                                </template>
                                <template #append-inner>
                                    <v-menu
                                        v-model="colorMenu"
                                        :close-on-content-click="false"
                                        location="bottom end"
                                    >
                                        <template #activator="{ props: menu }">
                                            <v-btn
                                                v-bind="menu"
                                                size="small"
                                                variant="tonal"
                                                :color="accent"
                                                prepend-icon="mdi-palette"
                                            >
                                                Choisir
                                            </v-btn>
                                        </template>
                                        <v-card rounded="lg">
                                            <v-color-picker
                                                v-model="pickerColor"
                                                mode="hex"
                                                :modes="['hex', 'rgb', 'hsl']"
                                                show-swatches
                                                :swatches="colorSwatches"
                                                swatches-max-height="180"
                                                elevation="0"
                                            />
                                            <v-card-actions>
                                                <v-btn
                                                    v-if="saved?.color"
                                                    variant="text"
                                                    prepend-icon="mdi-undo"
                                                    @click="
                                                        form.color =
                                                            saved?.color ?? ''
                                                    "
                                                >
                                                    Couleur enregistrée
                                                </v-btn>
                                                <v-spacer />
                                                <v-btn
                                                    :color="accent"
                                                    variant="flat"
                                                    @click="colorMenu = false"
                                                >
                                                    OK
                                                </v-btn>
                                            </v-card-actions>
                                        </v-card>
                                    </v-menu>
                                </template>
                            </v-text-field>

                            <!-- Apercu de l'application aux couleurs choisies -->
                            <div
                                v-if="form.color && colorValid"
                                class="color-preview"
                            >
                                <div
                                    class="color-preview-bar"
                                    :style="{
                                        background: accent,
                                        color: onAccent,
                                    }"
                                >
                                    <v-icon size="18" :color="onAccent"
                                        >mdi-menu</v-icon
                                    >
                                    <span class="font-weight-medium">
                                        {{ form.name || "Lol@pp" }}
                                    </span>
                                </div>
                                <div
                                    class="pa-3 d-flex flex-wrap align-center ga-2"
                                >
                                    <v-btn
                                        size="small"
                                        :color="accent"
                                        variant="flat"
                                    >
                                        Bouton
                                    </v-btn>
                                    <v-btn
                                        size="small"
                                        :color="accent"
                                        variant="outlined"
                                    >
                                        Contour
                                    </v-btn>
                                    <v-chip
                                        size="small"
                                        :color="accent"
                                        variant="tonal"
                                    >
                                        Puce
                                    </v-chip>
                                    <span
                                        class="text-body-2 font-weight-medium"
                                        :style="{ color: accent }"
                                    >
                                        Lien
                                    </span>
                                </div>
                                <div
                                    class="px-3 pb-2 d-flex align-center ga-1 text-caption"
                                    :class="`text-${contrastLevel.color}`"
                                >
                                    <v-icon size="16">{{
                                        contrastLevel.icon
                                    }}</v-icon>
                                    {{ contrastLevel.text }} (contraste
                                    {{ contrastWithWhite.toFixed(1) }})
                                </div>
                            </div>
                        </div>
                    </v-col>

                    <!-- Liaison Lola -->
                    <v-col cols="12" lg="6">
                        <div
                            class="cg-section"
                            :style="{ '--cg-color': accent }"
                        >
                            <div class="cg-section-title">
                                <v-avatar
                                    size="32"
                                    :color="accent"
                                    variant="tonal"
                                >
                                    <v-icon size="18">mdi-lan-connect</v-icon>
                                </v-avatar>
                                Liaison Lola
                            </div>
                            <v-select
                                v-model="form.webservice_id"
                                :items="webserviceItems"
                                item-title="title"
                                item-value="value"
                                label="Webservice"
                                prepend-inner-icon="mdi-api"
                                variant="outlined"
                                density="comfortable"
                                :color="accent"
                                clearable
                                :error-messages="fieldError('webservice_id')"
                            >
                                <template #item="{ props: itemProps, item }">
                                    <v-list-item
                                        v-bind="itemProps"
                                        :subtitle="item.raw.subtitle"
                                        :prepend-icon="
                                            item.raw.value === 5
                                                ? 'mdi-lightning-bolt'
                                                : 'mdi-server'
                                        "
                                        :base-color="
                                            item.raw.value === 5
                                                ? 'teal'
                                                : 'orange-darken-2'
                                        "
                                    />
                                </template>
                            </v-select>
                            <v-text-field
                                v-model="form.database_server"
                                label="Serveur Lola (HFSQL)"
                                prepend-inner-icon="mdi-server-network"
                                variant="outlined"
                                density="comfortable"
                                :color="accent"
                                hint="IP ou nom, avec le port s'il n'est pas 4900 (ex. 81.250.163.104:4905)"
                                persistent-hint
                                class="mb-2"
                                :error-messages="fieldError('database_server')"
                            />
                            <v-text-field
                                v-model="form.database_name"
                                label="Base"
                                prepend-inner-icon="mdi-database"
                                variant="outlined"
                                density="comfortable"
                                :color="accent"
                                placeholder="Donnees"
                                hint="Vide = Donnees (base par défaut)"
                                persistent-hint
                                :error-messages="fieldError('database_name')"
                            />
                            <v-alert
                                v-if="selectedWebservice && !isLightrest"
                                type="warning"
                                variant="tonal"
                                density="compact"
                                class="mt-3"
                                icon="mdi-alert-outline"
                            >
                                Webservice WebDev : le paiement en ligne
                                Fintecture et l'import des devis nomades exigent
                                LightREST (Lola 2027).
                            </v-alert>

                            <!-- Diagnostic -->
                            <div
                                v-if="!isCreate"
                                class="cg-diagnostic mt-4 pa-3 rounded-lg"
                            >
                                <div class="d-flex align-center flex-wrap ga-2">
                                    <v-icon :color="accent"
                                        >mdi-stethoscope</v-icon
                                    >
                                    <span class="font-weight-medium"
                                        >Diagnostic de liaison</span
                                    >
                                    <v-spacer />
                                    <v-btn
                                        size="small"
                                        :color="accent"
                                        variant="flat"
                                        prepend-icon="mdi-play"
                                        :loading="diagnosing"
                                        @click="runDiagnostic"
                                    >
                                        Tester
                                    </v-btn>
                                </div>
                                <div
                                    v-if="routingChanged"
                                    class="text-caption text-warning mt-2"
                                >
                                    <v-icon size="14" color="warning"
                                        >mdi-alert</v-icon
                                    >
                                    Le test porte sur la fiche enregistrée :
                                    enregistrer d'abord les modifications de
                                    liaison.
                                </div>
                                <div
                                    v-if="diagnosing"
                                    class="text-caption text-medium-emphasis mt-2"
                                >
                                    Jusqu'à 15 s : certains webservices sont
                                    lents à répondre.
                                </div>
                                <template v-if="diagnostic">
                                    <v-alert
                                        v-if="diagnostic.incomplete"
                                        type="warning"
                                        variant="tonal"
                                        density="compact"
                                        class="mt-2"
                                    >
                                        {{ diagnostic.message }}
                                    </v-alert>
                                    <v-list
                                        v-else
                                        density="compact"
                                        bg-color="transparent"
                                        class="mt-1 py-0"
                                    >
                                        <v-list-item
                                            v-if="diagnostic.port"
                                            class="px-0"
                                        >
                                            <template #prepend>
                                                <v-icon
                                                    :color="
                                                        diagnostic.port.success
                                                            ? 'success'
                                                            : 'warning'
                                                    "
                                                >
                                                    {{
                                                        diagnostic.port.success
                                                            ? "mdi-check-circle"
                                                            : "mdi-alert-circle"
                                                    }}
                                                </v-icon>
                                            </template>
                                            <v-list-item-title
                                                class="text-body-2 font-weight-medium"
                                            >
                                                Port
                                                {{ diagnostic.port.host }}:{{
                                                    diagnostic.port.port
                                                }}
                                                <span
                                                    class="text-caption text-medium-emphasis"
                                                    >(depuis le serveur de
                                                    l'API)</span
                                                >
                                            </v-list-item-title>
                                            <div class="text-caption cg-wrap">
                                                {{ diagnostic.port.message }}
                                            </div>
                                        </v-list-item>
                                        <v-list-item
                                            v-if="diagnostic.database"
                                            class="px-0"
                                        >
                                            <template #prepend>
                                                <v-icon
                                                    :color="
                                                        diagnostic.database
                                                            .success
                                                            ? 'success'
                                                            : 'error'
                                                    "
                                                >
                                                    {{
                                                        diagnostic.database
                                                            .success
                                                            ? "mdi-check-circle"
                                                            : "mdi-close-circle"
                                                    }}
                                                </v-icon>
                                            </template>
                                            <v-list-item-title
                                                class="text-body-2 font-weight-medium"
                                            >
                                                Requête via
                                                {{
                                                    diagnostic.database
                                                        .webservice
                                                }}
                                            </v-list-item-title>
                                            <div class="text-caption cg-wrap">
                                                {{
                                                    diagnostic.database.message
                                                }}
                                            </div>
                                        </v-list-item>
                                    </v-list>
                                    <div
                                        v-if="!diagnostic.incomplete"
                                        class="text-caption text-medium-emphasis"
                                    >
                                        C'est la requête via le webservice qui
                                        fait foi : le port peut être fermé pour
                                        l'API et ouvert pour le webservice.
                                    </div>
                                </template>
                            </div>
                        </div>
                    </v-col>

                    <!-- Google et reseaux -->
                    <v-col cols="12" lg="6">
                        <div
                            class="cg-section"
                            :style="{ '--cg-color': accent }"
                        >
                            <div class="cg-section-title">
                                <v-avatar
                                    size="32"
                                    :color="accent"
                                    variant="tonal"
                                >
                                    <v-icon size="18">mdi-google</v-icon>
                                </v-avatar>
                                Google et réseaux
                            </div>
                            <v-text-field
                                v-model="form.ggl_place_id"
                                label="Google Place ID"
                                prepend-inner-icon="mdi-google-maps"
                                variant="outlined"
                                density="comfortable"
                                :color="accent"
                                hint="Sert au lien de demande d'avis Google"
                                persistent-hint
                                class="mb-2"
                                :error-messages="fieldError('ggl_place_id')"
                            >
                                <template v-if="reviewUrl" #append-inner>
                                    <a
                                        :href="reviewUrl"
                                        target="_blank"
                                        rel="noopener"
                                        title="Ouvrir la page d'avis"
                                    >
                                        <v-icon size="small" :color="accent"
                                            >mdi-star-outline</v-icon
                                        >
                                    </a>
                                </template>
                            </v-text-field>
                            <v-text-field
                                v-model="form.fb_url"
                                label="Page Facebook"
                                prepend-inner-icon="mdi-facebook"
                                variant="outlined"
                                density="comfortable"
                                color="blue-darken-2"
                                :error-messages="fieldError('fb_url')"
                            />
                            <v-text-field
                                v-model="form.ggl_email"
                                label="E-mail Google"
                                prepend-inner-icon="mdi-email-outline"
                                variant="outlined"
                                density="comfortable"
                                :color="accent"
                                hint="Plus utilisé"
                                persistent-hint
                                :error-messages="fieldError('ggl_email')"
                            />
                        </div>
                    </v-col>

                    <!-- Mattermost -->
                    <v-col cols="12" lg="6">
                        <div
                            class="cg-section"
                            :style="{ '--cg-color': accent }"
                        >
                            <div class="cg-section-title">
                                <v-avatar
                                    size="32"
                                    :color="accent"
                                    variant="tonal"
                                >
                                    <v-icon size="18">mdi-forum</v-icon>
                                </v-avatar>
                                Mattermost
                            </div>
                            <v-text-field
                                v-model="form.team_id_mattermost"
                                label="Équipe Mattermost (identifiant)"
                                prepend-inner-icon="mdi-account-group"
                                variant="outlined"
                                density="comfortable"
                                :color="accent"
                                hint="Équipe où le bot publie les messages de l'entreprise"
                                persistent-hint
                                :error-messages="
                                    fieldError('team_id_mattermost')
                                "
                            />
                        </div>
                    </v-col>

                    <!-- Retour d'intervention (F5) : mail au secretariat -->
                    <v-col cols="12" lg="6">
                        <div
                            class="cg-section"
                            :style="{ '--cg-color': accent }"
                        >
                            <div class="cg-section-title">
                                <v-avatar
                                    size="32"
                                    :color="accent"
                                    variant="tonal"
                                >
                                    <v-icon size="18"
                                        >mdi-email-fast-outline</v-icon
                                    >
                                </v-avatar>
                                Retour d'intervention
                            </div>
                            <v-switch
                                v-model="form.intervention_mail_actif"
                                label="Envoyer un mail à chaque retour d'intervention"
                                :color="accent"
                                hide-details
                                inset
                                class="mb-2"
                            />
                            <v-text-field
                                v-model="form.intervention_mail_destinataires"
                                label="Destinataires"
                                prepend-inner-icon="mdi-email-outline"
                                variant="outlined"
                                density="comfortable"
                                :color="accent"
                                :disabled="!form.intervention_mail_actif"
                                hint="Une ou plusieurs adresses séparées par « ; ». Vide : adresse du siège de l'agence du projet (Lola)."
                                persistent-hint
                                :error-messages="
                                    fieldError(
                                        'intervention_mail_destinataires',
                                    )
                                "
                            />
                            <p
                                class="text-caption text-medium-emphasis mt-2 mb-0"
                            >
                                Trois messages possibles : intervention terminée
                                (sans PV), PV signé (PV joint), intervention non
                                terminée (nouvelle intervention et commande de
                                matériel).
                            </p>
                        </div>
                    </v-col>
                </v-row>

                <!-- Actions -->
                <div
                    class="cg-actions d-flex flex-wrap align-center ga-2 mt-4 pt-3"
                >
                    <v-btn
                        type="submit"
                        :color="accent"
                        variant="flat"
                        size="large"
                        :prepend-icon="
                            isCreate ? 'mdi-plus-circle' : 'mdi-content-save'
                        "
                        :loading="saving"
                        class="text-white"
                    >
                        {{ isCreate ? "Créer l'entreprise" : "Enregistrer" }}
                    </v-btn>
                    <v-btn
                        v-if="!isCreate"
                        variant="text"
                        prepend-icon="mdi-undo"
                        :disabled="saving"
                        @click="load"
                    >
                        Annuler les modifications
                    </v-btn>
                    <v-spacer />
                    <span
                        v-if="keyChanged || deactivating"
                        class="text-caption text-warning"
                    >
                        <v-icon size="14" color="warning">mdi-alert</v-icon>
                        Changement sensible : une confirmation sera demandée.
                    </span>
                </div>
            </v-form>
        </v-card-text>
    </v-card>
</template>

<style src="./admin-form.css"></style>

<style scoped>
    .color-dot {
        display: inline-block;
        width: 24px;
        height: 24px;
        border-radius: 6px;
        border: 1px solid rgba(0, 0, 0, 0.2);
        cursor: pointer;
        flex-shrink: 0;
    }
    .color-preview {
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
        background: white;
    }
    .color-preview-bar {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        font-size: 0.875rem;
    }
</style>
